import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { projectDataClient } from '../../../../lib/connect/clients';
import { sourceChangeSince } from './sectionRevision';
/**
 * Says when the solicitation of record was reissued (an amendment) after the
 * section's latest revision was written. Acknowledging records a locked review
 * snapshot against the new source; that revision is newer than the change, so
 * the banner clears.
 */ export function SourceChangeBanner({ projectId, sectionId, refreshKey, beforeAcknowledge, onAcknowledged }) {
    const [change, setChange] = useState(null);
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState(false);
    useEffect(()=>{
        let live = true;
        void (async ()=>{
            try {
                const [versions, review] = await Promise.all([
                    projectDataClient.listSectionVersions({
                        projectId,
                        sectionId,
                        limit: 1
                    }),
                    projectDataClient.readSolicitationReview({
                        projectId
                    })
                ]);
                const found = sourceChangeSince(versions.versions?.[0]?.createdAt?.toDate(), review.state?.versions ?? []);
                if (live) setChange(found ? {
                    ...found
                } : null);
            } catch  {
                // The banner is advisory; an unreadable source history never blocks writing.
                if (live) setChange(null);
            }
        })();
        return ()=>{
            live = false;
        };
    }, [
        projectId,
        sectionId,
        refreshKey
    ]);
    if (!change) return null;
    const acknowledge = async ()=>{
        setBusy(true);
        setFailed(false);
        try {
            if (beforeAcknowledge && !await beforeAcknowledge()) return;
            await projectDataClient.createReviewSnapshot({
                projectId,
                sectionId,
                label: `Reviewed against ${change.sourceName} (version ${change.versionNumber})`,
                comment: 'Acknowledged the change to the solicitation of record.'
            });
            setChange(null);
            onAcknowledged();
        } catch  {
            setFailed(true);
        } finally{
            setBusy(false);
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        role: "alert",
        "data-testid": Testids.proposalVersions.sourceChangeBanner,
        className: "border-b border-warning bg-warning-muted px-4 py-2 text-xs text-warning",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "font-semibold",
                children: "The solicitation changed after this section was last written."
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "mt-0.5",
                children: [
                    change.sourceName,
                    " (version ",
                    change.versionNumber,
                    ") was issued as the source of record after the latest revision. Check that this section still answers it."
                ]
            }),
            failed && /*#__PURE__*/ _jsx("p", {
                className: "mt-0.5",
                children: "Couldn't record the acknowledgement. Try again."
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-1.5 flex flex-wrap gap-2",
                children: [
                    /*#__PURE__*/ _jsx(Link, {
                        to: `/proposal/${projectId}/v2/sources/history`,
                        "data-testid": Testids.proposalVersions.sourceChangeReview,
                        className: "rounded border border-border px-2 py-1 font-medium hover:bg-muted",
                        children: "Review the change"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "default",
                        size: "sm",
                        type: "button",
                        disabled: busy,
                        "data-testid": Testids.proposalVersions.sourceChangeAcknowledge,
                        onClick: acknowledge,
                        children: "Acknowledge as reviewed"
                    })
                ]
            })
        ]
    });
}
